"use client";

import { getRuntimeEnvironment } from "@/utils/helpers/runtimeEnvironment";
import { useCompatRouter as useRouter } from "@/lib/compat-router/context";
import { useState } from "react";
import { PropertyTypeLabels } from "@/types/property-config";

export const searchQueryPlaceholder = "e.g, 2 bedroom for rent in Lekki...";

const runtime = getRuntimeEnvironment();
// 1. Tab Configurations
export const SearchListingType = {
  SALE: "sale",
  RENT: "rent",
  SHORTLET: "shortlet",
  LAND: "land",
  MANAGED_PROPERTIES: "managed-properties",
  MANAGED_DEVELOPMENTS: "managed-developments",
} as const;

type SearchListingTypeValue =
  (typeof SearchListingType)[keyof typeof SearchListingType];

export const SearchListingLabels = {
  [SearchListingType.SALE]: "Buy",
  [SearchListingType.RENT]: "Rent",
  [SearchListingType.SHORTLET]: "Shortlet",
  [SearchListingType.LAND]: "Land",
  [SearchListingType.MANAGED_PROPERTIES]: "Managed Properties",
  [SearchListingType.MANAGED_DEVELOPMENTS]: "Managed Developments",
} as const;

// 2. Dynamic Option Configuration Types
export type SearchSegment = "livingSpaces" | "land" | "developments";

export interface FilterOption {
  label: string;
  inputType: "text" | "select";
  dropdownList?: { label: string; value: string }[];
  placeholder?: string;
  defaultValue?: string;
}

// HomeSearchFormData type aligned with existing search components
export interface HomeSearchFormData {
  searchQuery?: string;
  location?: string;
  bedroomCount?: string;
  bathroomCount?: string;
  listingType?: string;
  propertyType?: string;
  minPrice?: string;
  maxPrice?: string;
  furnished?: string;
  serviced?: string;
  shared?: string;
  addedToSite?: string;
  priceRange?: string;
  state?: string;
  city?: string;
  developmentType?: string;
  minInvestment?: string;
}

// 3. Consolidated Configuration for All Filter Options
const PROPERTY_TYPE_OPTIONS: { label: string; value: string }[] = [
  { label: "Any", value: "" },
  ...Object.entries(PropertyTypeLabels).map(([value, label]) => ({
    label,
    value,
  })),
];

export const allOptionsConfig: Record<string, FilterOption> = {
  location: {
    label: "Location",
    inputType: "text",
    placeholder: "E.g. Lagos",
    defaultValue: "Lagos",
  },
  propertyType: {
    label: "Property Type",
    inputType: "select",
    dropdownList: PROPERTY_TYPE_OPTIONS,
  },
  bedroomCount: {
    label: "Bedrooms",
    inputType: "select",
    dropdownList: [
      { label: "Any", value: "" },
      { label: "1", value: "1" },
      { label: "2", value: "2" },
      { label: "3", value: "3" },
      { label: "4", value: "4" },
      { label: "5", value: "5" },
      { label: "6", value: "6" },
      { label: "7", value: "7" },
      { label: "8", value: "8" },
      { label: "9", value: "9" },
      { label: "10", value: "10" },
      { label: "10+", value: "10" },
    ],
  },
  bathroomCount: {
    label: "Bathrooms",
    inputType: "select",
    dropdownList: [
      { label: "Any", value: "" },
      { label: "1", value: "1" },
      { label: "2", value: "2" },
      { label: "3", value: "3" },
      { label: "4", value: "4" },
      { label: "5", value: "5" },
      { label: "6", value: "6" },
      { label: "7", value: "7" },
      { label: "8", value: "8" },
      { label: "9", value: "9" },
      { label: "10", value: "10" },
      { label: "10+", value: "10" },
    ],
  },
  minPrice: {
    label: "Min. Budget",
    inputType: "select",
    dropdownList: [
      { label: "Any", value: "" },
      { label: "50 Thousand", value: "50000" },
      { label: "100 Thousand", value: "100000" },
      { label: "200 Thousand", value: "200000" },
      { label: "500 Thousand", value: "500000" },
      { label: "1 Million", value: "1000000" },
      { label: "10 Million", value: "10000000" },
      { label: "20 Million", value: "20000000" },
      { label: "50 Million", value: "50000000" },
      { label: "100 Million", value: "100000000" },
      { label: "200 Million", value: "200000000" },
      { label: "500 Million", value: "500000000" },
    ],
  },

  maxPrice: {
    label: "Max. Budget",
    inputType: "select",
    dropdownList: [
      { label: "Any", value: "" },
      { label: "100 Thousand", value: "100000" },
      { label: "200 Thousand", value: "200000" },
      { label: "500 Thousand", value: "500000" },
      { label: "1 Million", value: "1000000" },
      { label: "2 Million+", value: "2000000" },
      { label: "20 Million", value: "20000000" },
      { label: "50 Million", value: "50000000" },
      { label: "100 Million", value: "100000000" },
      { label: "200 Million", value: "200000000" },
      { label: "500 Million", value: "500000000" },
      { label: "1 Billion+", value: "1000000000" },
    ],
  },
  furnished: {
    label: "Furnished",
    inputType: "select",
    dropdownList: [
      { label: "Any", value: "" },
      { label: "Yes", value: "yes" },
      { label: "No", value: "no" },
    ],
  },
  serviced: {
    label: "Serviced",
    inputType: "select",
    dropdownList: [
      { label: "Any", value: "" },
      { label: "Yes", value: "yes" },
      { label: "No", value: "no" },
    ],
  },
  shared: {
    label: "Shared",
    inputType: "select",
    dropdownList: [
      { label: "Any", value: "" },
      { label: "Yes", value: "yes" },
      { label: "No", value: "no" },
    ],
  },
  addedToSite: {
    label: "Added to site",
    inputType: "select",
    dropdownList: [
      { label: "Any", value: "" },
      { label: "Last 24 hours", value: "24h" },
      { label: "Last week", value: "week" },
      { label: "Last month", value: "month" },
      { label: "Last 3 months", value: "3months" },
    ],
  },
  listingType: {
    label: "Listing Type",
    inputType: "select",
    dropdownList: [
      { label: "Any", value: "" },
      { label: "Rent", value: "rent" },
      { label: "Sale", value: "sale" },
      { label: "Shortlet", value: "shortlet" },
    ],
  },
  priceRange: {
    label: "Price Range",
    inputType: "select",
    dropdownList: [
      { label: "Any", value: "" },
      { label: "Under ₦1M", value: "under-1m" },
      { label: "₦1M - ₦5M", value: "1m-5m" },
      { label: "₦5M - ₦10M", value: "5m-10m" },
      { label: "₦10M - ₦20M", value: "10m-20m" },
      { label: "₦20M - ₦50M", value: "20m-50m" },
      { label: "₦50M - ₦100M", value: "50m-100m" },
      { label: "₦100M+", value: "100m-plus" },
    ],
  },
  developmentType: {
    label: "Development Type",
    inputType: "select",
    dropdownList: [
      { label: "Any", value: "" },
      { label: "Joint-Venture", value: "joint-venture" },
      { label: "Off-Plan Sale", value: "off-plan-sale" },
      { label: "Sponsorship Contract", value: "sponsorship-contract" },
      { label: "Fractional Ownership", value: "fractional-ownership" },
    ],
  },
  minInvestment: {
    label: "Minimum Subscription (₦)",
    inputType: "select",
    dropdownList: [
      { label: "Any", value: "" },
      { label: "1m - 10m", value: "1m-10m" },
      { label: "10m - 20m", value: "10m-20m" },
      { label: "21m - 50m", value: "21m-50m" },
      { label: "51m - 100m", value: "51m-100m" },
      { label: "100,000,000+", value: "100000000" },
    ],
  },
  state: {
    label: "State",
    inputType: "text",
    placeholder: "E.g. Lagos",
  },
  city: {
    label: "City",
    inputType: "text",
    placeholder: "E.g. Ikeja",
  },
};

// 4. Mapping Segment layouts
export const searchSegmentsOptions: Record<
  SearchSegment,
  {
    tabAssociations: SearchListingTypeValue[];
    primaryFields: (keyof typeof allOptionsConfig)[];
    extraFields: (keyof typeof allOptionsConfig)[];
  }
> = {
  livingSpaces: {
    tabAssociations: [
      SearchListingType.SALE,
      SearchListingType.RENT,
      SearchListingType.SHORTLET,
      SearchListingType.MANAGED_PROPERTIES,
    ],
    primaryFields: [
      "location",
      "propertyType",
      "bedroomCount",
      "minPrice",
      "maxPrice",
    ],
    extraFields: ["bathroomCount", "furnished", "serviced", "shared"],
  },
  land: {
    tabAssociations: [SearchListingType.LAND],
    primaryFields: ["location", "minPrice", "maxPrice"],
    extraFields: [],
  },
  developments: {
    tabAssociations: [SearchListingType.MANAGED_DEVELOPMENTS],
    primaryFields: ["location", "propertyType", "minPrice", "maxPrice"],
    extraFields: ["serviced"],
  },
};
// Route mapping configuration based on active tab
const SEARCH_ROUTES: Record<SearchListingTypeValue, string> = {
  [SearchListingType.SALE]: "/properties",
  [SearchListingType.RENT]: "/properties",
  [SearchListingType.SHORTLET]: "/properties",
  [SearchListingType.LAND]: "/properties",
  [SearchListingType.MANAGED_PROPERTIES]: "/managed-properties",
  [SearchListingType.MANAGED_DEVELOPMENTS]: "/managed-developments",
};

const HomeHeroFilter = () => {
  const router = useRouter();
  const [activeTab, setActiveTab] = useState<SearchListingTypeValue>(
    SearchListingType.SALE,
  );
  const [showMoreOptions, setShowMoreOptions] = useState(false);
  const [isMobileFilterOpen, setIsMobileFilterOpen] = useState(false);
  const [formValues, setFormValues] = useState<HomeSearchFormData>({
    location: "Lagos",
  });

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;
    setFormValues((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const basePath = SEARCH_ROUTES[activeTab] || "/properties";

    // Fast path: Route immediately if a search query exists
    if (formValues.searchQuery?.trim()) {
      const searchParams = new URLSearchParams({
        searchQuery: formValues.searchQuery.trim(),
      });
      router.push(`${basePath}?${searchParams.toString()}`);
      return;
    }

    // Normal flow: Build full search params
    const searchParams = new URLSearchParams();

    if (activeTab) {
      if (activeTab === SearchListingType.LAND) {
        searchParams.append("propertyCategory", activeTab);
      } else {
        searchParams.append("listingType", activeTab);
      }
    }

    const activeSegmentKey =
      (Object.keys(searchSegmentsOptions) as SearchSegment[]).find((key) =>
        searchSegmentsOptions[key].tabAssociations.includes(activeTab),
      ) || "livingSpaces";

    const currentSegment = searchSegmentsOptions[activeSegmentKey];

    const allowedFields = new Set<string>([
      ...currentSegment.primaryFields,
      ...currentSegment.extraFields,
    ]);

    Object.entries(formValues).forEach(([key, value]) => {
      if (value && value !== "" && allowedFields.has(key)) {
        const numericValue =
          typeof value === "string" ? value.replace(/,/g, "") : value;
        searchParams.append(key, String(numericValue));
      }
    });

    const finalUrl = `${basePath}?${searchParams.toString()}`;
    router.push(finalUrl);
  };

  // Determine current search segment dynamically from our config map
  const activeSegmentKey =
    (Object.keys(searchSegmentsOptions) as SearchSegment[]).find((key) =>
      searchSegmentsOptions[key].tabAssociations.includes(activeTab),
    ) || "livingSpaces";

  const currentSegment = searchSegmentsOptions[activeSegmentKey];

  const renderField = (fieldKey: keyof typeof allOptionsConfig) => {
    const config = allOptionsConfig[fieldKey];
    if (!config) return null;

    return (
      <div key={fieldKey} className="flex flex-col gap-2">
        <label className="text-xs font-bold text-white tracking-wide">
          {config.label}
        </label>
        <div className="relative">
          {config.inputType === "text" ? (
            <input
              type="text"
              name={fieldKey}
              value={formValues[fieldKey as keyof HomeSearchFormData] ?? ""}
              onChange={handleChange}
              placeholder={config.placeholder}
              className="w-full bg-[#0a2530] text-white border border-white/20 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[#1fa3b3] placeholder-white/40"
            />
          ) : (
            <>
              <select
                name={fieldKey}
                value={formValues[fieldKey as keyof HomeSearchFormData] ?? ""}
                onChange={handleChange}
                className="w-full appearance-none bg-[#0a2530] text-white border border-white/20 rounded-xl px-4 py-3 pr-10 text-sm focus:outline-none focus:border-[#1fa3b3]"
              >
                {config.dropdownList?.map((option) => (
                  <option key={option.label} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-white/60">
                <svg
                  className="fill-current h-4 w-4"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                >
                  <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                </svg>
              </div>
            </>
          )}
        </div>
      </div>
    );
  };

  const renderTabs = () => (
    <div className="flex border-b border-white/10 mb-6 overflow-x-auto scrollbar-none">
      <div className="flex gap-2">
        {Object.entries(SearchListingType).map(([key, value]) => {
          const isActive = activeTab === value;
          if (runtime.isProduction && value === "managed-developments") {
            return null;
          }
          return (
            <button
              key={value}
              type="button"
              onClick={() => {
                setActiveTab(value);
                setShowMoreOptions(false);
              }}
              className={`py-3 px-6 text-sm font-semibold transition-all duration-150 rounded-t-xl border-b-2 outline-none whitespace-nowrap ${
                isActive
                  ? "border-[#1fa3b3] text-[#1fa3b3] bg-white/5"
                  : "border-transparent text-white/80 hover:text-white"
              }`}
            >
              {SearchListingLabels[value]}
            </button>
          );
        })}
      </div>
    </div>
  );

  return (
    <div className="w-full max-w-6xl mx-auto bg-[#0c2e3a] text-white rounded-3xl p-6 md:p-8 shadow-2xl border border-[#163e4c]">
      {/* 2. Responsive Form Grid */}
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Mobile search bar */}
        <div className="flex items-center gap-2 lg:hidden">
          <input
            type="search"
            name="searchQuery"
            value={formValues.searchQuery ?? ""}
            onChange={handleChange}
            placeholder={searchQueryPlaceholder}
            className="h-13 flex-1 min-w-0 bg-[#0a2530] text-white border border-white/20 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[#1fa3b3] placeholder-white/40"
          />
          <button
            type="submit"
            className="flex items-center justify-center gap-2 bg-[#1fa3b3] hover:bg-[#188c9a] text-white font-bold px-4 py-3 rounded-xl shrink-0 transition-colors duration-150"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              ></path>
            </svg>
            <span className="hidden">Search</span>
          </button>
          <button
            type="button"
            onClick={() => setIsMobileFilterOpen(!isMobileFilterOpen)}
            aria-label={isMobileFilterOpen ? "Hide filters" : "Show filters"}
            className={`flex items-center justify-center px-4 py-3 rounded-xl border shrink-0 transition-colors duration-150 ${
              isMobileFilterOpen
                ? "bg-[#1fa3b3] border-[#1fa3b3] text-white"
                : "bg-[#0a2530] border-white/20 text-white hover:border-[#1fa3b3]"
            }`}
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M10.5 6h9.75M10.5 6a1.5 1.5 0 11-3 0m3 0a1.5 1.5 0 10-3 0M3.75 6H7.5m3 12h9.75m-9.75 0a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m-3.75 0H7.5m9-6h3.75m-3.75 0a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m-9.75 0h9.75"
              />
            </svg>
          </button>
        </div>
        {isMobileFilterOpen && <div className="lg:hidden">{renderTabs()}</div>}
        {/* Mobile expanded filters */}
        {isMobileFilterOpen && (
          <div className="lg:hidden space-y-6 animate-fadeIn">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {currentSegment.primaryFields.map(renderField)}
            </div>

            {showMoreOptions && (
              <div className="pt-6 border-t border-white/10 grid grid-cols-1 sm:grid-cols-2 gap-4 animate-fadeIn">
                {currentSegment.extraFields.map(renderField)}
              </div>
            )}

            {currentSegment.extraFields.length > 0 && (
              <button
                type="button"
                onClick={() => setShowMoreOptions(!showMoreOptions)}
                className="flex items-center gap-2 text-sm text-white/80 hover:text-white font-medium focus:outline-none transition-colors"
              >
                <span>More search options</span>
                <svg
                  className={`h-4 w-4 transform transition-transform duration-200 ${showMoreOptions ? "rotate-180" : ""}`}
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>
            )}
          </div>
        )}

        {/* Desktop layout */}
        <div className="hidden lg:block space-y-6">
          {renderTabs()}

          {/* Dynamic primary input layout */}
          <div
            className={`grid grid-cols-1 sm:grid-cols-2 gap-4 ${
              currentSegment.primaryFields.length === 3
                ? "md:grid-cols-3"
                : currentSegment.primaryFields.length === 4
                  ? "md:grid-cols-4"
                  : "md:grid-cols-5"
            }`}
          >
            {currentSegment.primaryFields.map(renderField)}
          </div>

          {/* 4. Dynamic expansion drawer for secondary filters */}
          {showMoreOptions &&
            (() => {
              const fieldCount = currentSegment.extraFields.length;

              if (fieldCount === 0) return null;

              const gridColsClass = `grid-cols-1 sm:grid-cols-${Math.min(2, fieldCount)} md:grid-cols-${Math.min(4, fieldCount)}`;

              return (
                <div
                  className={`pt-6 border-t border-white/10 grid ${gridColsClass} gap-4 animate-fadeIn`}
                >
                  {currentSegment.extraFields.map(renderField)}
                </div>
              );
            })()}

          {/* 3. Footer Operations Row (More options toggle + Search Button) */}
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4 pt-2">
            <button
              type="button"
              onClick={() => setShowMoreOptions(!showMoreOptions)}
              className="flex items-center gap-2 text-sm text-white/80 hover:text-white font-medium focus:outline-none transition-colors"
            >
              <span>More search options</span>
              <svg
                className={`h-4 w-4 transform transition-transform duration-200 ${showMoreOptions ? "rotate-180" : ""}`}
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
            </button>

            {/* Primary Search Button */}
            <button
              type="submit"
              className="w-full sm:w-auto bg-[#1fa3b3] hover:bg-[#188c9a] text-white font-bold px-8 py-3.5 rounded-xl flex items-center justify-center gap-2 shadow-lg transition-colors duration-150"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                ></path>
              </svg>
              <span>Search</span>
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default HomeHeroFilter;
